/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { ROUTES } from '../constants/routes';
import { Purpose, type AccessTokenResponse } from '../managers/sso';

export const INCOMPLETE_SOCIAL_AUTH = {
  USER_NOT_REGISTERED: 'USER_NOT_REGISTERED',
  USER_NOT_VERIFIED: 'USER_NOT_VERIFIED',
} as const;

export type IncompleteSocialAuthReason =
  (typeof INCOMPLETE_SOCIAL_AUTH)[keyof typeof INCOMPLETE_SOCIAL_AUTH];

export type SocialIncompleteAuthData = {
  method: 'google';
  incompleteReason: IncompleteSocialAuthReason;
  firstName?: string;
  lastName?: string;
  state?: string;
  email?: string;
  otpEmail?: string;
  gender?: string;
  dialingCode?: string;
  mobilePhone?: string;
};

export type GoogleAuthExchangeResult =
  | { kind: 'authenticated'; data: AccessTokenResponse }
  | { kind: 'incomplete'; data: SocialIncompleteAuthData }
  | { kind: 'failed' };

export function isIncompleteSocialAuth(
  data: unknown,
): data is SocialIncompleteAuthData {
  if (!data || typeof data !== 'object' || !('incompleteReason' in data)) {
    return false;
  }
  const reason = (data as SocialIncompleteAuthData).incompleteReason;
  return (
    reason === INCOMPLETE_SOCIAL_AUTH.USER_NOT_REGISTERED ||
    reason === INCOMPLETE_SOCIAL_AUTH.USER_NOT_VERIFIED
  );
}

function hasGoogleAuthTokens(data: AccessTokenResponse): boolean {
  return typeof data.refreshToken === 'string' && data.refreshToken.length > 0;
}

function hasGoogleAuthName(data: AccessTokenResponse): boolean {
  return Boolean(data.firstName?.trim() || data.lastName?.trim());
}

// 200 + tokens = signed in; 202 = incomplete; 200 without tokens = failed.
export function interpretGoogleAuthExchange(
  status: number,
  data: AccessTokenResponse,
  email?: string,
): GoogleAuthExchangeResult {
  if (status === 202) {
    return {
      kind: 'incomplete',
      data: toIncompleteAuth(data, email),
    };
  }

  if (status === 200 && hasGoogleAuthTokens(data)) {
    return { kind: 'authenticated', data };
  }

  if (status === 200) {
    return { kind: 'failed' };
  }

  return { kind: 'failed' };
}

function toIncompleteAuth(
  data: AccessTokenResponse,
  email?: string,
): SocialIncompleteAuthData {
  return {
    method: 'google',
    incompleteReason: hasGoogleAuthName(data)
      ? INCOMPLETE_SOCIAL_AUTH.USER_NOT_VERIFIED
      : INCOMPLETE_SOCIAL_AUTH.USER_NOT_REGISTERED,
    firstName: data.firstName,
    lastName: data.lastName,
    state: data.state,
    email: email ?? data.otpEmail,
    otpEmail: data.otpEmail,
    gender: data.gender,
    dialingCode: data.dialingCode,
    mobilePhone: data.mobilePhone,
  };
}

// Only unverified users need email OTP; unregistered goes via useFinishSignIn.
export function getSocialAuthOtpRoute(
  data: SocialIncompleteAuthData,
  isSignup: boolean,
): { pathname: string; params: Record<string, string> } | undefined {
  if (data.incompleteReason !== INCOMPLETE_SOCIAL_AUTH.USER_NOT_VERIFIED) {
    return undefined;
  }

  return {
    pathname: ROUTES.ACCOUNT_VERIFY_EMAIL,
    params: {
      email: data.email ?? data.otpEmail ?? '',
      purpose: isSignup
        ? Purpose.EMAIL_OTP_VERIFICATION_SIGNUP
        : Purpose.EMAIL_OTP_VERIFICATION_LOGIN,
    },
  };
}
